/**
 * License Activation Flow
 * Orchestrates the complete license activation process
 */

import type { 
  DeviceInfo, 
  LicenseActivationResult,
  LicenseValidationError,
  LicenseValidationErrorType
} from '$lib/types/subscription';

import type {
  LicenseActivationFlow,
  LicenseValidator,
  LicenseProgressCallback,
  SecureLicenseStorage,
  DeviceLimitManager,
  OfflineGracePeriodManager
} from './interfaces';

/**
 * Pre-activation check result
 */
interface PreActivationCheckResult {
  canActivate: boolean;
  warnings: string[];
  errors: string[];
  deviceInfo?: DeviceInfo;
  conflictingDevices?: DeviceInfo[];
}

/**
 * Activation attempt record for analytics
 */
interface ActivationAttempt {
  licenseKey: string;
  timestamp: number;
  deviceId: string;
  result: 'success' | 'failure' | 'cancelled';
  error?: string;
  duration: number;
}

export class LicenseActivationFlowImpl implements LicenseActivationFlow {
  private readonly validator: LicenseValidator;
  private readonly storage: SecureLicenseStorage;
  private readonly deviceManager: DeviceLimitManager;
  private readonly gracePeriodManager: OfflineGracePeriodManager;

  // Activation attempt tracking
  private readonly ATTEMPT_STORAGE_KEY = 'license_activation_attempts';
  private readonly MAX_STORED_ATTEMPTS = 50;

  constructor(
    validator: LicenseValidator,
    storage: SecureLicenseStorage,
    deviceManager: DeviceLimitManager,
    gracePeriodManager: OfflineGracePeriodManager
  ) {
    this.validator = validator;
    this.storage = storage;
    this.deviceManager = deviceManager;
    this.gracePeriodManager = gracePeriodManager;
  }

  /**
   * Complete license activation process
   */
  async activateLicense(
    licenseKey: string,
    options: {
      forceActivation?: boolean;
      skipDeviceCheck?: boolean;
      progressCallback?: LicenseProgressCallback;
    } = {}
  ): Promise<LicenseActivationResult> {
    const startTime = Date.now();
    const { forceActivation = false, skipDeviceCheck = false, progressCallback } = options;
    
    try {
      progressCallback?.({ stage: 'validation', message: 'Starting license activation...', progress: 0 });

      // Record activation attempt
      const deviceInfo = await this.deviceManager.getCurrentDeviceInfo();
      const attemptId = this.recordActivationAttempt(licenseKey, deviceInfo.deviceId, 'start');

      // Step 1: Validate license format
      progressCallback?.({ stage: 'validation', message: '라이선스 형식 확인 중...', progress: 10 });
      
      if (!this.validateLicenseFormat(licenseKey)) {
        const error = this.createError('invalid_format', '라이선스 키 형식이 올바르지 않습니다');
        this.completeActivationAttempt(attemptId, 'failure', error.message);
        return { success: false, error };
      }

      // Step 2: Pre-activation checks
      progressCallback?.({ stage: 'validation', message: '사전 활성화 검사 중...', progress: 20 });
      
      const preCheckResult = await this.performPreActivationChecks(licenseKey);
      if (!preCheckResult.canActivate && !forceActivation) {
        const error = this.createError(
          'validation_failed', 
          preCheckResult.errors.join('; ')
        );
        this.completeActivationAttempt(attemptId, 'failure', error.message);
        return { success: false, error };
      }

      // Step 3: Handle device limit conflicts if necessary
      if (!skipDeviceCheck && !forceActivation) {
        progressCallback?.({ stage: 'validation', message: '기기 제한 확인 중...', progress: 30 });
        
        const conflictResult = await this.handleDeviceLimitConflict(licenseKey, deviceInfo);
        if (conflictResult.action === 'deny') {
          const error = this.createError('device_limit_exceeded', conflictResult.message);
          this.completeActivationAttempt(attemptId, 'failure', error.message);
          return { 
            success: false, 
            error,
            requiresDeviceDeactivation: true,
            deviceInfo
          };
        }
      }

      // Step 4: Attempt activation with retry logic
      progressCallback?.({ stage: 'activation', message: '라이선스 활성화 중...', progress: 50 });
      
      const activationResult = await this.attemptActivationWithRetry(
        licenseKey, 
        deviceInfo, 
        progressCallback
      );

      if (activationResult.success) {
        // Step 5: Post-activation setup
        progressCallback?.({ stage: 'cleanup', message: '활성화 완료 중...', progress: 90 });
        
        await this.performPostActivationSetup(licenseKey);
        
        progressCallback?.({ stage: 'cleanup', message: '활성화 완료!', progress: 100 });
        
        this.completeActivationAttempt(attemptId, 'success');
        return activationResult;
      } else {
        this.completeActivationAttempt(attemptId, 'failure', activationResult.error?.message);
        return activationResult;
      }

    } catch (error) {
      const activationError = this.createError(
        'unknown_error', 
        error instanceof Error ? error.message : '예상치 못한 활성화 오류가 발생했습니다'
      );
      
      progressCallback?.({ stage: 'cleanup', message: '활성화 실패', progress: 100 });
      
      return { success: false, error: activationError };
    }
  }

  /**
   * Validate license format before activation
   */
  validateLicenseFormat(licenseKey: string): boolean {
    if (!licenseKey || typeof licenseKey !== 'string') {
      return false;
    }

    // Remove whitespace and convert to uppercase
    const cleanKey = licenseKey.trim().toUpperCase();

    // Check for common license key formats
    const formats = [
      /^[A-Z0-9]{8}-[A-Z0-9]{8}-[A-Z0-9]{8}-[A-Z0-9]{8}$/, // Gumroad: XXXXXXXX-XXXXXXXX-XXXXXXXX-XXXXXXXX (4 groups of 8)
      /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/, // XXXX-XXXX-XXXX-XXXX (4 groups of 4)
      /^[A-Z0-9]{8}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{12}$/, // UUID format
      /^[A-Z0-9]{16,64}$/, // Simple alphanumeric (16-64 chars)
      /^[A-Z0-9]{8}-[A-Z0-9]{8}-[A-Z0-9]{8}$/, // Three 8-char segments
      /^BETA-[A-Z0-9]+-[A-Z0-9]+(-[A-Z0-9]+)*$/ // BETA-userId-expiryDate-signature or BETA-userId-signature
    ];

    return formats.some(format => format.test(cleanKey));
  }

  /**
   * Pre-activation checks
   */
  async performPreActivationChecks(licenseKey: string): Promise<PreActivationCheckResult> {
    const warnings: string[] = [];
    const errors: string[] = [];
    let canActivate = true;

    try {
      // Check if license already exists
      const existingLicense = await this.storage.getLicense(licenseKey);
      if (existingLicense) {
        warnings.push('License is already activated on this device');
      }

      // Get current device info
      const deviceInfo = await this.deviceManager.getCurrentDeviceInfo();

      // Check device limits
      const canAddDevice = await this.validator.canActivateOnDevice(licenseKey, deviceInfo);
      if (!canAddDevice) {
        const activeDevices = await this.deviceManager.getActiveDevices(licenseKey);
        errors.push(`Device limit reached. Currently ${activeDevices.length} devices active.`);
        canActivate = false;

        return {
          canActivate,
          warnings,
          errors,
          deviceInfo,
          conflictingDevices: activeDevices
        };
      }

      // Check network connectivity (optional)
      const isOnline = navigator.onLine;
      if (!isOnline) {
        warnings.push('Device appears to be offline. Activation may use cached validation.');
      }

      return {
        canActivate,
        warnings,
        errors,
        deviceInfo
      };

    } catch (error) {
      errors.push(`Pre-activation check failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return {
        canActivate: false,
        warnings,
        errors
      };
    }
  }

  /**
   * Handle device limit conflicts
   */
  async handleDeviceLimitConflict(
    licenseKey: string,
    deviceInfo: DeviceInfo
  ): Promise<{
    action: 'allow' | 'deny' | 'prompt_deactivation';
    message: string;
    conflictingDevices?: DeviceInfo[];
  }> {
    try {
      const canActivate = await this.validator.canActivateOnDevice(licenseKey, deviceInfo);
      
      if (canActivate) {
        return {
          action: 'allow',
          message: 'Device can be activated'
        };
      }

      const activeDevices = await this.deviceManager.getActiveDevices(licenseKey);
      const currentDeviceExists = activeDevices.find(d => d.deviceId === deviceInfo.deviceId);

      if (currentDeviceExists) {
        return {
          action: 'allow',
          message: 'Current device is already registered'
        };
      }

      // Find inactive devices that can be deactivated
      const now = Date.now();
      const inactiveDevices = activeDevices.filter(device => {
        const daysSinceActive = (now - device.lastActiveAt.getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceActive > 30; // Consider inactive after 30 days
      });

      if (inactiveDevices.length > 0) {
        return {
          action: 'prompt_deactivation',
          message: `Device limit reached, but ${inactiveDevices.length} inactive device(s) can be deactivated`,
          conflictingDevices: inactiveDevices
        };
      }

      return {
        action: 'deny',
        message: 'Device limit reached and all devices are currently active',
        conflictingDevices: activeDevices
      };

    } catch (error) {
      return {
        action: 'deny',
        message: `Failed to check device conflicts: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Attempt activation with retry logic
   */
  private async attemptActivationWithRetry(
    licenseKey: string,
    deviceInfo: DeviceInfo,
    progressCallback?: LicenseProgressCallback
  ): Promise<LicenseActivationResult> {
    const maxAttempts = 3;
    const baseDelay = 1000; // 1 second

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        progressCallback?.({ 
          stage: 'activation', 
          message: `Activation attempt ${attempt} of ${maxAttempts}...`, 
          progress: 50 + (attempt - 1) * 10 
        });

        const result = await this.validator.activateLicense(
          licenseKey, 
          deviceInfo, 
          progressCallback
        );

        if (result.success) {
          return result;
        }

        // Check if error is retryable
        if (result.error && !result.error.retryable) {
          return result;
        }

        // Wait before retry (exponential backoff)
        if (attempt < maxAttempts) {
          const delay = baseDelay * Math.pow(2, attempt - 1);
          progressCallback?.({ 
            stage: 'activation', 
            message: `Retrying in ${delay/1000} seconds...`, 
            progress: 50 + attempt * 10 
          });
          
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          return result;
        }

      } catch (error) {
        if (attempt === maxAttempts) {
          return {
            success: false,
            error: this.createError('unknown_error', error instanceof Error ? error.message : 'Activation failed')
          };
        }
      }
    }

    return {
      success: false,
      error: this.createError('validation_failed', 'All activation attempts failed')
    };
  }

  /**
   * Perform post-activation setup
   */
  private async performPostActivationSetup(licenseKey: string): Promise<void> {
    try {
      // End any existing grace period
      await this.gracePeriodManager.endGracePeriod(licenseKey);

      // Update device activity
      const deviceInfo = await this.deviceManager.getCurrentDeviceInfo();
      await this.deviceManager.updateDeviceActivity(licenseKey, deviceInfo.deviceId);

      // Clean up old grace periods
      await this.gracePeriodManager.cleanExpiredGracePeriods();

    } catch (error) {
      console.warn('Post-activation setup encountered issues:', error);
      // Don't fail activation for post-setup issues
    }
  }

  /**
   * Helper methods for activation attempt tracking
   */
  
  private recordActivationAttempt(licenseKey: string, deviceId: string, phase: 'start'): string {
    const attemptId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      const attempts = this.getStoredAttempts();
      const attempt: ActivationAttempt = {
        licenseKey: this.hashLicenseKey(licenseKey), // Don't store actual key
        timestamp: Date.now(),
        deviceId: deviceId.substring(0, 8) + '...', // Partial device ID
        result: 'failure', // Will be updated
        duration: 0
      };

      attempts.push({ id: attemptId, attempt });
      
      // Keep only recent attempts
      if (attempts.length > this.MAX_STORED_ATTEMPTS) {
        attempts.splice(0, attempts.length - this.MAX_STORED_ATTEMPTS);
      }

      localStorage.setItem(this.ATTEMPT_STORAGE_KEY, JSON.stringify(attempts));
    } catch (error) {
      console.warn('Failed to record activation attempt:', error);
    }

    return attemptId;
  }

  private completeActivationAttempt(attemptId: string, result: 'success' | 'failure', error?: string): void {
    try {
      const attempts = this.getStoredAttempts();
      const attemptIndex = attempts.findIndex(a => a.id === attemptId);
      
      if (attemptIndex >= 0) {
        const attempt = attempts[attemptIndex];
        attempt.attempt.result = result;
        attempt.attempt.duration = Date.now() - attempt.attempt.timestamp;
        
        if (error) {
          attempt.attempt.error = error;
        }

        localStorage.setItem(this.ATTEMPT_STORAGE_KEY, JSON.stringify(attempts));
      }
    } catch (error) {
      console.warn('Failed to complete activation attempt record:', error);
    }
  }

  private getStoredAttempts(): Array<{ id: string; attempt: ActivationAttempt }> {
    try {
      const stored = localStorage.getItem(this.ATTEMPT_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  }

  private hashLicenseKey(licenseKey: string): string {
    // Simple hash for privacy (don't store actual license keys)
    return licenseKey.substring(0, 4) + '***' + licenseKey.substring(licenseKey.length - 4);
  }

  private createError(type: LicenseValidationErrorType, message: string): LicenseValidationError {
    const retryableTypes: LicenseValidationErrorType[] = [
      'network_error',
      'server_error',
      'rate_limited'
    ];

    return {
      type,
      message,
      retryable: retryableTypes.includes(type),
      details: {}
    };
  }
}